// Company Theme System
// Supports multiple color palettes and company-specific theming
import { BrandTheme, ThemeConfig } from '@dynamic-flow/types';
import { brandBasicThemes } from './brandBasicThemes';

// Re-export advanced color system
export * from './colorSystem';
export * from './brandBasicThemes';

// Utility functions
export function getTheme(themeId: BrandTheme): ThemeConfig {
  return brandBasicThemes[themeId] || brandBasicThemes.bh;
}

// Resolve theme with advanced theme override
export function resolveTheme(flowTheme: ThemeConfig): ThemeConfig {
  // If there's advanced theme data, override the basic theme colors
  if (flowTheme?.advanced && flowTheme.advanced.colors) {
    const advanced = flowTheme.advanced;

    return {
      ...flowTheme,
      colors: {
        primary: {
          main: advanced.colors.primary.baseColor.hex,
          light: advanced.colors.primary.variations[60].hex,
          dark: advanced.colors.primary.variations[120].hex,
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: advanced.colors.secondary.baseColor.hex,
          light: advanced.colors.secondary.variations[60].hex,
          dark: advanced.colors.secondary.variations[120].hex,
          contrastText: '#FFFFFF',
        },
        background: {
          default: flowTheme.colors.background.default ?? '#FFFFFF',
          paper: flowTheme.colors.background.paper ?? '#FFFFFF',
          accent: flowTheme.colors.background.accent ?? '#FFFFFF',
        },
        text: {
          primary:
            flowTheme.colors.text.primary ||
            advanced.colors.neutral.variations[120].hex,
          secondary:
            flowTheme.colors.text.secondary ||
            advanced.colors.neutral.variations[100].hex,
          disabled:
            flowTheme.colors.text.disabled ||
            advanced.colors.neutral.variations[60].hex,
        },
        border: {
          light:
            advanced.colors.neutral.variations[20]?.hex ||
            advanced.colors.neutral.variations[60].hex,
          main: advanced.colors.neutral.variations[60].hex,
          dark: advanced.colors.neutral.variations[100].hex,
        },
      },
    };
  }

  // Return basic theme if no advanced theme data
  return flowTheme;
}

export function getAllThemes(): ThemeConfig[] {
  // Convert brand basic themes to full ThemeConfig format
  return Object.values(brandBasicThemes).map((flatTheme) => ({
    ...flatTheme,
    colors: {
      primary: {
        main: flatTheme.colors.primary.main,
        light: flatTheme.colors.primary.light,
        dark: flatTheme.colors.primary.dark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: flatTheme.colors.secondary.main,
        light: flatTheme.colors.secondary.light,
        dark: flatTheme.colors.secondary.dark,
        contrastText: flatTheme.colors.secondary.contrastText ?? '#FFFFFF',
      },
      background: {
        default: flatTheme.colors.background.default,
        paper: flatTheme.colors.background.paper,
        accent: flatTheme.colors.background.accent ?? '#F3F4F6',
      },
      text: {
        primary: flatTheme.colors.text.primary,
        secondary: flatTheme.colors.text.secondary,
        disabled: flatTheme.colors.text.disabled ?? '#9CA3AF',
      },
      border: {
        light: flatTheme.colors.border.light,
        main: flatTheme.colors.border.main,
        dark: flatTheme.colors.border.dark,
      },
    },
  }));
}

// CSS Variable generator
export function generateCSSVariables(theme: ThemeConfig): string {
  return `
    :root {
      /* Colors */
      --color-primary: ${theme.colors.primary.main};
      --color-primary-light: ${theme.colors.primary.light};
      --color-primary-dark: ${theme.colors.primary.dark};
      --color-primary-contrast: ${theme.colors.primary.contrastText};
      
      --color-secondary: ${theme.colors.secondary.main};
      --color-secondary-light: ${theme.colors.secondary.light};
      --color-secondary-dark: ${theme.colors.secondary.dark};
      --color-secondary-contrast: ${theme.colors.secondary.contrastText};
      
      --color-background: ${theme.colors.background.default};
      --color-background-paper: ${theme.colors.background.paper};
      --color-background-accent: ${theme.colors.background.accent};
      
      --color-text-primary: ${theme.colors.text.primary};
      --color-text-secondary: ${theme.colors.text.secondary};
      --color-text-disabled: ${theme.colors.text.disabled};
      
      --color-border-light: ${theme.colors.border.light};
      --color-border-main: ${theme.colors.border.main};
      --color-border-dark: ${theme.colors.border.dark};
      
      /* Typography */
      --font-family: ${theme.typography.fontFamily};
      --font-size-xs: ${theme.typography.fontSizes.xs};
      --font-size-sm: ${theme.typography.fontSizes.sm};
      --font-size-base: ${theme.typography.fontSizes.base};
      --font-size-lg: ${theme.typography.fontSizes.lg};
      --font-size-xl: ${theme.typography.fontSizes.xl};
      --font-size-2xl: ${theme.typography.fontSizes['2xl']};
      --font-size-3xl: ${theme.typography.fontSizes['3xl']};
      
      --font-weight-normal: ${theme.typography.fontWeights.normal};
      --font-weight-medium: ${theme.typography.fontWeights.medium};
      --font-weight-semibold: ${theme.typography.fontWeights.semibold};
      --font-weight-bold: ${theme.typography.fontWeights.bold};
      
      /* Spacing */
      --spacing-xs: ${theme.spacing.xs};
      --spacing-sm: ${theme.spacing.sm};
      --spacing-md: ${theme.spacing.md};
      --spacing-lg: ${theme.spacing.lg};
      --spacing-xl: ${theme.spacing.xl};
      --spacing-2xl: ${theme.spacing['2xl']};
      
      /* Border Radius */
      --border-radius-sm: ${theme.borderRadius.sm};
      --border-radius-md: ${theme.borderRadius.md};
      --border-radius-lg: ${theme.borderRadius.lg};
      --border-radius-xl: ${theme.borderRadius.xl};
      
      /* Shadows */
      --shadow-sm: ${theme.shadows.sm};
      --shadow-md: ${theme.shadows.md};
      --shadow-lg: ${theme.shadows.lg};
    }
  `;
}
